import type { MaybeRefOrGetter, Ref } from "vue";
import { nextTick, readonly, ref, toValue, watch } from "vue";
import { authLoaderClock } from "./authLoaderClock";
import { APP_READY_CLASS } from "./bootLoaderScript";

/** Shows the loader from index.html while `visible` is true and hides it otherwise. */
export function useBootLoader(visible: MaybeRefOrGetter<boolean>): void {
  watch(
    () => toValue(visible),
    (isVisible) => {
      document.documentElement.classList.toggle(APP_READY_CLASS, !isVisible);
      authLoaderClock.noteBootLoaderHidden(!isVisible);
    },
    { immediate: true },
  );
}

/**
 * False until the router has resolved the first route and Vue has rendered it. Keep the index.html loader up until
 * then: hiding it earlier shows an empty page while the route's code loads, and on a login step the page's own
 * loader would then start again instead of taking over.
 */
export function useFirstRouteRendered(router: { isReady: () => Promise<unknown> }): Readonly<Ref<boolean>> {
  const rendered = ref(false);

  void router.isReady()
    .then(() => nextTick())
    // A failed first navigation is reported by the router itself; the app must still leave the loader.
    .catch(() => undefined)
    .then(() => {
      rendered.value = true;
    });

  return readonly(rendered);
}
